<script>
  import QRCode from 'qrcode';
  import { copyToClipboard } from '@/helpers/utils';

  import GroupSelect from '@/components/form/GroupSelect.vue';
  import ChangeCrypto from '@/components/dialogs/ChangeCrypto.vue';
  import AlertIcon from '@/assets/images/svg/icons/alert-white.svg';
  import ExclamationIcon from '@/assets/images/svg/icons/icon-exclamation.svg';
  import LoadingSpinner from '@/components/base/LoadingSpinner.vue';

  export default {
    name: 'PayWithCrypto',

    components: {
      LoadingSpinner,
      ChangeCrypto,
      GroupSelect,
      AlertIcon,
      ExclamationIcon,
    },

    data: () => ({
      qrCodeUrl: '',
      estimatedAmount: '',
      address: '',
      exchangeRate: null,
      expiryData: {
        estimatedDate: null,
        remainingTimeText: null,
        timerInterval: null,
        textClass: '',
      },
      selectedCurrency: null,
      previousCurrency: null,
      minimalPaymentAmount: null,
      loadings: {
        currencies: false,
        estimatedAmount: false,
        address: false,
      },

      newTransactionTimer: null,

      changeCryptoDialog: false,
    }),

    computed: {
      currencies() {
        return this.$store.state.cashierCurrencies.filter((group) => group.text === 'Crypto') || [];
      },

      networkName() {
        if (!this.selectedCurrency) return '';

        const regex = /\((.*?)\)/g;
        const subtitle = this.selectedCurrency.subtitle;
        const matches = [];
        let match;

        while ((match = regex.exec(subtitle)) !== null) {
          matches.push(match[1]);
        }

        if (!matches.length) return subtitle;
        return matches[0];
      },
    },

    mounted() {
      this.checkAndShowOptions();
    },

    methods: {
      async checkAndShowOptions() {
        if (!this.$refs.currencyDropdown) return;

        if (!this.selectedCurrency) {
          await this.$nextTick();
          this.$refs.currencyDropdown.menu = true;
        }
      },

      onCurrencySelected(selectedCurrency) {
        if (JSON.stringify(this.selectedCurrency) === JSON.stringify(selectedCurrency)) {
          return;
        }
        this.selectedCurrency = selectedCurrency;
        if (this.previousCurrency) {
          this.$refs.currencyDropdown.selectedItem = this.previousCurrency;
          this.changeCryptoDialog = true;
        } else {
          this.setCurrency();
        }
      },

      async requestInvoice() {
        this.loadings.address = true;
        this.loadings.estimatedAmount = true;

        try {
          const responseData = await this.$store.dispatch('requestPaymentInvoice', {
            currency: this.selectedCurrency.value,
          });
          const response = responseData.data;

          if (response?.haserror) {
            await this.$store.dispatch('showAlertSnackBar', {
              text: response.message || 'Request Failed. Try again later.',
            });
          }

          if (!response?.haserror) {
            this.address = response?.data?.pay_address;
            this.estimatedAmount = response?.data?.pay_amount;
            this.exchangeRate = response?.data?.exchange_rate;
            this.minimalPaymentAmount = response?.data?.minimal_payment_amount;
            this.expiryData.estimatedDate = response?.data?.expiration_estimate_date;
            if (this.expiryData.estimatedDate) {
              this.startTimerFrom(this.expiryData.estimatedDate);
            }
            await this.$store.dispatch('subscribeToOrderStatus', response?.data?.order_id);
          }

          await this.generateQRCode();
        } catch (e) {
          console.error(e);
          await this.$store.dispatch('showAlertSnackBar', {
            text: 'Request Failed. Try again later.',
          });
        }

        this.loadings.address = false;
        this.loadings.estimatedAmount = false;
      },

      async generateQRCode() {
        try {
          this.qrCodeUrl = await QRCode.toDataURL(this.address, {
            errorCorrectionLevel: 'H',
          });
        } catch (err) {
          this.qrCodeUrl = '';
          console.error(err);
        }
      },

      setCurrency() {
        this.previousCurrency = { ...this.selectedCurrency };
        this.requestInvoice();
      },

      returnCurrency() {
        this.selectedCurrency = { ...this.previousCurrency };
      },

      async copyToClipboard(value) {
        try {
          await copyToClipboard(value);
          await this.$store.dispatch('showAlertSnackBar', {
            text: 'Copied to clipboard',
          });
        } catch (error) {
          console.error(error);
        }
      },

      async toTransactionsHistory() {
        await this.$router.push('/profileuser/history');
        await this.$store.dispatch('closeCashierDialog');
      },

      formatExchangeRate(value) {
        const rate = parseFloat(value);
        if (isNaN(rate)) return '0.00';

        if (rate < 0.01 && rate !== 0) {
          return rate.toFixed(8);
        } else {
          return rate.toFixed(2);
        }
      },

      startTimerFrom(date) {
        clearInterval(this.expiryData.timerInterval);
        this.expiryData.timerInterval = setInterval(() => {
          let future = Date.parse(date);
          let now = new Date();
          let diff = future - now;
          let hours = Math.floor(diff / (1000 * 60 * 60));
          let mins = Math.floor(diff / (1000 * 60));
          let secs = Math.floor(diff / 1000);

          let m = mins - hours * 60;
          let s = secs - mins * 60;
          this.expiryData.remainingTimeText = m + ':' + (s < 10 ? '0' + s : s);
          if (m < 5) {
            this.expiryData.textClass = 'warning--text';
          }
          if (diff < 0) {
            clearInterval(this.expiryData.timerInterval);
            this.expiryData.textClass = 'error';
            this.expiryData.remainingTimeText = 'Expired, requesting new invoice';
            this.qrCodeUrl = '';
            this.estimatedAmount = '';
            this.address = 'Requesting';
            setTimeout(this.requestInvoice, 5000);
          }
        }, 1000);
      },

      beforeDestroy() {
        clearInterval(this.expiryData.timerInterval);
      },
    },
  };
</script>

<template>
  <div class="pay-with-crypto">
    <GroupSelect
      ref="currencyDropdown"
      :value="selectedCurrency"
      :items="currencies"
      label="Select Currency"
      @select="onCurrencySelected"
    />

    <div v-if="selectedCurrency && !loadings.address" class="invoice-details mt-3">
      <div class="exchange-rate">
        <b>Exchange rate:</b>
        <span>1 {{ selectedCurrency?.text }} = ${{ formatExchangeRate(exchangeRate) }}</span>
      </div>

      <div class="min-deposit-warning">
        <AlertIcon />
        <b>
          Deposits less than {{ minimalPaymentAmount }} {{ selectedCurrency?.text }} will NOT be credited to your
          balance
        </b>
      </div>

      <div class="crypto-address">
        <b>Address:</b>
        <br />
        {{ address }}
      </div>

      <button class="copy-button" @click="copyToClipboard(address)">
        <span>Copy {{ selectedCurrency?.text }} address</span>
      </button>

      <div class="position-relative d-flex justify-center align-center my-3">
        <v-expand-transition>
          <img v-if="qrCodeUrl" :src="qrCodeUrl" alt="crypto address qr code" class="position-absolute qr-code" />
        </v-expand-transition>
        <div class="qr-code"></div>
      </div>

      <div class="one-time-warning">
        <ExclamationIcon />
        <span>
          Top up addresses are
          <b>ONE</b>
          time use only! Don’t reuse it, as your account won’t be credited.
        </span>
      </div>
      <div class="time-network">
        <div v-if="expiryData.estimatedDate" class="time-wrapper" :class="expiryData.textClass">
          <span v-if="expiryData.textClass !== 'error'">Invoice expires in:</span>
          <b>{{ expiryData.remainingTimeText }}</b>
        </div>
        <div class="network-wrapper">
          <span>Network:</span>
          <b>{{ networkName }} Network</b>
        </div>
      </div>
      <div class="additional-info mt-3">
        Send {{ selectedCurrency.text }} ONLY on the {{ networkName }} network to this address or your funds may be
        lost.
      </div>
      <div class="additional-info">
        {{ selectedCurrency.text }} deposits can take some time to process on the blockchain. You can check its status
        on your
        <span class="link" @click="toTransactionsHistory">transactions</span>
        page.
      </div>
    </div>
    <div v-if="loadings.address" class="spinner-wrapper">
      <LoadingSpinner />
    </div>

    <ChangeCrypto
      v-if="changeCryptoDialog"
      v-model="changeCryptoDialog"
      :currency="previousCurrency || {}"
      @confirm="setCurrency"
      @deny="returnCurrency"
    />
  </div>
</template>

<style lang="scss">
  .pay-with-crypto {
    ::v-deep .grouped-menu-btn {
      box-shadow: none !important;
      border-radius: 6px;

      .selected-item {
        gap: 6px;
        font-weight: 500;
      }
    }

    .invoice-details {
      display: flex;
      flex-direction: column;
      gap: 10px;

      .exchange-rate {
        color: #fff;
        font-size: 12px;
        font-weight: 400;
        line-height: 120%;
        text-align: center;
      }
    }

    .min-deposit-warning {
      display: flex;
      width: 100%;
      padding: 8px 10px;
      align-items: center;
      gap: 10px;
      border-radius: 6px;
      background: #612492;

      svg {
        max-width: 20px;
      }

      b {
        color: #fff;
        font-size: 12px;
        font-weight: 700;
        line-height: 16px;
      }
    }

    .crypto-address {
      color: #fff;
      font-size: 12px;
      font-style: normal;
      font-weight: 400;
      line-height: 16px;

      b {
        font-weight: 600;
      }
    }

    .copy-button {
      display: flex;
      width: 100%;
      padding: 16px 32px;
      justify-content: center;
      align-items: center;
      gap: 4px;
      border-radius: 6px;
      background: var(--Primary, #f8bf1e);

      &:hover {
        filter: brightness(104%);
      }

      &:active {
        filter: brightness(93%);
      }

      span {
        color: #140324;
        text-align: center;
        font-family: 'Inter', sans-serif;
        font-size: 16px;
        font-weight: 700;
        line-height: 100%;
      }
    }

    .qr-code {
      display: block;
      width: 160px;
      height: 160px;
      margin-inline: auto;
      border-radius: 10px;
    }

    .time-network {
      margin-top: -10px;
      .network-wrapper,
      .time-wrapper {
        display: flex;
        gap: 8px;
        padding: 12px 16px;
        align-items: center;
        align-self: stretch;
        color: #fff;
        font-size: 14px;
        line-height: 120%;
        border-bottom: 1px solid rgba(#fff, 0.3);

        b {
          font-weight: 500;
        }
      }
      .time-wrapper {
        &.warning--text,
        &.error {
          b {
            font-weight: 600;
          }
        }
      }
    }

    .one-time-warning {
      display: flex;
      padding: 8px 10px;
      align-items: center;
      gap: 10px;
      align-self: stretch;
      border-radius: 6px;
      background: rgba(255, 255, 255, 0.2);

      svg {
        width: 22px;
        height: 22px;
      }

      span {
        color: #fff;
        font-size: 12px;
        font-weight: 600;
        line-height: 150%;
      }

      b {
        font-weight: 700;
      }
    }

    .additional-info {
      color: #fff;
      font-size: 12px;
      font-weight: 400;
      line-height: 150%;

      .link {
        text-decoration: underline;
        cursor: pointer;
      }
    }

    .spinner-wrapper {
      margin-top: 25%;
    }
  }
</style>
